import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords = '',
  ogTitle,
  ogDescription,
  ogImage = 'https://idnty.one/idnty-logo.svg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage = 'https://idnty.one/idnty-logo.svg',
  canonicalUrl,
  noindex = false,
  structuredData = null,
  children
}) => {
  const siteTitle = 'IDNTY';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'One unified identity platform that secures access, builds trust with your prospects, and scales with your growth — delivered in weeks, not months.';
  const metaDescription = description || defaultDescription;
  const metaOgTitle = ogTitle || title || siteTitle;
  const metaOgDescription = ogDescription || metaDescription;
  const metaTwitterTitle = twitterTitle || ogTitle || title || siteTitle;
  const metaTwitterDescription = twitterDescription || ogDescription || metaDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={metaOgTitle} />
      <meta property="og:description" content={metaOgDescription} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:title" content={metaTwitterTitle} />
      <meta property="twitter:description" content={metaTwitterDescription} />
      {twitterImage && <meta property="twitter:image" content={twitterImage} />}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

// Helper function to generate Article structured data for blog posts
export const generateArticleStructuredData = ({
  headline,
  description,
  image,
  author = 'IDNTY Team',
  datePublished,
  dateModified,
  url
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  image: image || 'https://idnty.one/idnty-logo.svg',
  author: {
    '@type': 'Organization',
    name: author
  },
  publisher: {
    '@type': 'Organization',
    name: 'IDNTY',
    logo: {
      '@type': 'ImageObject',
      url: 'https://idnty.one/idnty-logo.svg'
    }
  },
  datePublished,
  dateModified: dateModified || datePublished,
  url
});

// Helper function to generate Service structured data
export const generateServiceStructuredData = ({
  name,
  description,
  provider = 'IDNTY',
  url
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  provider: {
    '@type': 'Organization',
    name: provider
  },
  url
});

// Helper function to generate FAQPage structured data
export const generateFAQStructuredData = (questions) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map(q => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: q.answer
    }
  }))
});

// Helper function to generate BreadcrumbList structured data
export const generateBreadcrumbStructuredData = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

export default SEO;
